// saveQueue.mjs
//
// A tiny COALESCING debounce queue, extracted from App.jsx so its timing behavior is
// testable with REAL timers. The interactive server-sync paths (template edits, profile
// edits) used to fire one immediate whole-document write PER edit — reordering N cards =
// N overlapping read-modify-writes on one Blobs key → contention → 502/500. This queue
// collects edits over a short window (latest-wins per key) and flushes them as ONE batch,
// mirroring what the load-time resync already does.
//
// Why a module, not inline: a debounce that silently drops the LAST write is exactly the
// failure a pure-function unit test can't see — you have to exercise the actual setTimeout
// coalescing. Timer fns are injectable so a test can drive them deterministically.
//
// Invariant this must never break: after any burst of schedule() calls, a flush eventually
// delivers the LATEST value for every key touched — including the last one. Nothing is
// dropped unless the process dies before the timer fires (the caller's load-time resync is
// the backstop for that).

export function createCoalescingQueue({ delayMs = 600, flush, timers } = {}) {
  if (typeof flush !== "function") throw new Error("createCoalescingQueue: flush must be a function");
  const setT = (timers && timers.setTimeout) || setTimeout;
  const clearT = (timers && timers.clearTimeout) || clearTimeout;
  const pending = new Map(); // key(String) → latest value
  let timer = null;

  // Queue a value under `key` (latest-wins: a re-edit of the same key replaces its pending
  // copy, so the LAST value always ships) and (re)arm the debounce.
  function schedule(key, value) {
    pending.set(String(key), value);
    if (timer) clearT(timer);
    timer = setT(() => { run(); }, delayMs);
  }

  // Drain + flush everything pending. Awaitable so a critical path can force the queue out
  // BEFORE it inspects downstream state. Snapshots then CLEARS the map before awaiting, so
  // any edit arriving during the flush queues into the NEXT run rather than being lost or
  // double-sent. `opts` is forwarded to flush (e.g. { surface:false } to mute an alert).
  async function run(opts) {
    if (timer) { clearT(timer); timer = null; }
    if (!pending.size) return;
    const batch = [...pending.values()];
    pending.clear();
    await flush(batch, opts);
  }

  return { schedule, flush: run, size: () => pending.size, pendingKeys: () => [...pending.keys()] };
}
